import { Button } from '@/components/ui/button'
import { Drawer, DrawerContentRight } from '@/components/ui/drawer'
import { SectionHeading } from '@/components/ui/section'
import { useSettingsPanelState } from '@/hooks/useSettingsPanelState'
import { useLocations } from '@/queries/locations/useLocations'
import { useStytchOrganization } from '@stytch/react/b2b'
import type { WorkspaceLocation } from 'honeygrid-types'
import { LayoutGrid, Plus, Table2 } from 'lucide-react'

import { LocationRecordPanel } from './LocationRecordPanel'
import { LocationsCards } from './LocationsCards'
import { LocationsTable } from './LocationsTable'

export const LocationsList = () => {
	const { organization } = useStytchOrganization()
	const { data: locations, isLoading } = useLocations({
		workspaceId: organization?.organization_id,
	})
	const { state, openRecord, openCreate, closePanel, setView } = useSettingsPanelState('locations')

	const selectedLocation = state.recordId
		? locations?.find((l) => l.uuid === state.recordId)
		: undefined

	const handleSelectLocation = (location: WorkspaceLocation) => {
		openRecord(location.uuid)
	}

	const handleAddLocation = () => {
		openCreate()
	}

	const listPanel = (
		<div className="flex flex-col h-full">
			{/* Header with title and controls in one line */}
			<div className="flex items-center justify-between mb-4">
				<SectionHeading>Locations</SectionHeading>
				<div className="flex items-center gap-3">
					{/* View Toggle */}
					<div className="flex border rounded-md">
						<Button
							variant={state.view === 'table' ? 'default' : 'ghost'}
							size="sm"
							onClick={() => setView('table')}
							className="rounded-r-none border-r"
						>
							<Table2 className="h-4 w-4" />
						</Button>
						<Button
							variant={state.view === 'cards' ? 'default' : 'ghost'}
							size="sm"
							onClick={() => setView('cards')}
							className="rounded-l-none"
						>
							<LayoutGrid className="h-4 w-4" />
						</Button>
					</div>
					<Button onClick={handleAddLocation} size="sm" className="cursor-pointer">
						<Plus className="h-4 w-4 mr-2" />
						Add Location
					</Button>
				</div>
			</div>

			{/* Content */}
			<div className="flex-1 overflow-auto">
				{isLoading ? (
					<div className="text-center py-8 text-muted-foreground">Loading locations...</div>
				) : state.view === 'table' ? (
					<LocationsTable
						locations={locations || []}
						onSelectLocation={handleSelectLocation}
						onAddLocation={handleAddLocation}
						selectedLocationId={state.recordId || undefined}
					/>
				) : (
					<LocationsCards
						locations={locations || []}
						onSelectLocation={handleSelectLocation}
						onAddLocation={handleAddLocation}
						selectedLocationId={state.recordId || undefined}
					/>
				)}
			</div>
		</div>
	)

	return (
		<div className="flex flex-col h-full">
			{listPanel}
			<Drawer open={state.isOpen} onOpenChange={(open) => !open && closePanel()} direction="right">
				<DrawerContentRight>
					<LocationRecordPanel
						onClose={closePanel}
						location={selectedLocation}
						isNew={state.mode === 'new'}
					/>
				</DrawerContentRight>
			</Drawer>
		</div>
	)
}
